import { ChangeEvent, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';

import { MutationStatusCode, Payment } from '../gql-types.generated';

// The service reason code for a void that arrived after the processor's settlement batch closed.
// It is the one failure a merchant can act on, so it gets its own message.
const alreadySettledReasonCode = 'AlreadySettled';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogContent: {
      width: 420,

      [theme.breakpoints.down('md')]: {
        width: 300,
      },
    },
    reason: {
      marginTop: theme.spacing(2),
    },
    summary: {
      marginTop: theme.spacing(2),
    },
    requestList: {
      margin: theme.spacing(0.5, 0, 0),
      paddingLeft: theme.spacing(2.5),
    },
  }),
);

interface VoidPaymentProps {
  open: boolean;
  paymentRecord?: Payment;
  voidStatus?: string;
  voidError?: Error;
  voidErrorReasonCode?: string | null;
  sendVoid: (paymentId: string, reason: string) => void;
  clearVoidState: () => void;
  close: () => void;
  submit: () => void;
  defaultCurrency?: string | null;
}

// Long reference numbers are cut to their last nine characters, the same way the payment details
// dialog shows them, so a merchant can match the two.
const displayReference = (referenceNumber?: string | null) => {
  const trimmed = referenceNumber?.trim();
  if (!trimmed) return 'No reference number';
  return trimmed.length > 9 ? `***${trimmed.slice(-9)}` : trimmed;
};

const VoidPayment: React.FC<VoidPaymentProps> = props => {
  const styles = useStyles();
  const { open, paymentRecord, voidStatus, voidError, voidErrorReasonCode, sendVoid, clearVoidState, close, submit, defaultCurrency } =
    props;
  const [reason, setReason] = useState('');
  const [inFlight, setInFlight] = useState(false);

  useEffect(() => {
    if (!open) {
      setReason('');
      setInFlight(false);
    }
  }, [open]);

  // The thunk reports through redux rather than resolving, so completion is observed here.
  useEffect(() => {
    if (!inFlight) return;
    if (voidStatus === MutationStatusCode.Success) {
      setInFlight(false);
      submit();
    } else if (voidStatus === MutationStatusCode.Error || voidError) {
      setInFlight(false);
    }
  }, [voidStatus, voidError, inFlight, submit]);

  const onVoid = () => {
    if (!paymentRecord?.id) return;
    clearVoidState();
    setInFlight(true);
    sendVoid(paymentRecord.id, reason.trim());
  };

  const onClose = () => {
    clearVoidState();
    close();
  };

  const failed = !!voidError || voidStatus === MutationStatusCode.Error;
  const settledInTheGap = voidErrorReasonCode === alreadySettledReasonCode;

  const formatAmount = (amount?: number | null) =>
    new Intl.NumberFormat('en', {
      style: 'currency',
      currency: defaultCurrency || 'USD',
      currencyDisplay: 'symbol',
    }).format((amount || 0) / 100);

  // A void reverses the payment whole, so the merchant is shown everything it undoes: the card charge,
  // every payment request it paid (all of them for a consolidated payment), and any credit applied.
  const allocations = (paymentRecord?.paymentRequestAllocation ?? []).filter(allocation => !!allocation.paymentRequest);
  const creditAmount = paymentRecord?.creditAmount ?? 0;
  // Paid entirely with credit: nothing was charged, so there is nothing at the processor to cancel and no
  // amount to show - the void only reopens the requests and restores the credit.
  const creditOnly = paymentRecord?.amount === 0 && creditAmount > 0;

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="void-payment-title">
      <DialogTitle id="void-payment-title">Void payment</DialogTitle>
      <DialogContent className={styles.dialogContent}>
        {creditOnly ? (
          <Typography variant="body2">
            This payment was paid entirely with credit, so nothing was charged. Voiding it cancels the payment. This cannot be undone.
          </Typography>
        ) : (
          <>
            <Typography variant="body2">
              Voiding cancels this payment at the processor before it settles, so the cardholder is never charged and no refund fee
              applies. This cannot be undone.
            </Typography>

            <Typography variant="body2" className={styles.summary} data-cy="void-amount">
              Amount to be voided: <strong>{formatAmount(paymentRecord?.amount)}</strong>
            </Typography>
          </>
        )}

        {allocations.length > 0 && (
          <div className={styles.summary}>
            <Typography variant="body2">
              {allocations.length === 1
                ? 'This payment request will be reopened for payment:'
                : 'These payment requests will be reopened for payment:'}
            </Typography>
            <ul className={styles.requestList} data-cy="void-requests">
              {allocations.map(allocation => (
                <li key={allocation.paymentRequest?.id}>
                  <Typography variant="body2">
                    {displayReference(allocation.paymentRequest?.referenceNumber)} - {formatAmount(allocation.amount)}
                  </Typography>
                </li>
              ))}
            </ul>
          </div>
        )}

        {creditAmount > 0 && (
          <Typography variant="body2" className={styles.summary} data-cy="void-credit">
            The {formatAmount(creditAmount)} credit applied to this payment will be restored to the customer&apos;s credit balance.
          </Typography>
        )}

        {failed && (
          <Alert severity={settledInTheGap ? 'warning' : 'error'} data-cy="void-error">
            {settledInTheGap
              ? 'This payment has already settled and can no longer be voided. Issue a refund instead.'
              : voidError?.message || 'The payment could not be voided.'}
          </Alert>
        )}

        <TextField
          className={styles.reason}
          label="Reason"
          fullWidth
          required
          size="small"
          value={reason}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setReason(e.target.value)}
          inputProps={{ 'data-cy': 'void-reason' }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" data-cy="void-cancel">
          Cancel
        </Button>
        <Button
          onClick={onVoid}
          color="primary"
          variant="contained"
          disabled={!reason.trim() || inFlight || settledInTheGap}
          startIcon={inFlight ? <CircularProgress size={16} /> : null}
          data-cy="void-confirm"
        >
          Void payment
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VoidPayment;
